import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Mail, MapPin, Phone } from 'lucide-react'

import { BufferedTextInput } from '@/shared/ui/buffered-text-input'
import { Card, CardContent } from '@/shared/ui/card'
import { type SimplifiedClient } from '@monorepo/functions/src/types/client'
import { PersonIcon } from '@radix-ui/react-icons'

interface ClientCardProps {
    client: SimplifiedClient
    onClientChange: (client: SimplifiedClient) => void
}

export function ClientCard({ client, onClientChange }: ClientCardProps) {
    const { t } = useTranslation()
    const clientRef = useRef(client)
    clientRef.current = client

    const updateField = <K extends keyof SimplifiedClient>(field: K, value: SimplifiedClient[K]) => {
        onClientChange({ ...clientRef.current, [field]: value })
    }

    return (
        <Card className='w-full pt-6'>
            <CardContent>
                <div className='space-y-4'>
                    <div className='flex items-center justify-between'>
                        <span className='flex items-center gap-2'>
                            <PersonIcon className='h-4 w-4' />
                            {t('client.name', 'Nombre')}:
                        </span>
                        <div className='flex items-center justify-end gap-2'>
                            <BufferedTextInput
                                value={client.name}
                                onDebouncedChange={value => updateField('name', value)}
                                className='w-48'
                            />
                        </div>
                    </div>
                    <div className='flex items-center justify-between'>
                        <span className='flex items-center gap-2'>
                            <PersonIcon className='h-4 w-4' />
                            {t('client.lastName', 'Apellidos')}:
                        </span>
                        <div className='flex items-center justify-end gap-2'>
                            <BufferedTextInput
                                value={client.lastName}
                                onDebouncedChange={value => updateField('lastName', value)}
                                className='w-48'
                            />
                        </div>
                    </div>
                    <div className='flex items-center justify-between'>
                        <span className='flex items-center gap-2'>
                            <Phone className='h-4 w-4' />
                            {t('client.phone', 'Teléfono')}:
                        </span>
                        <div className='flex items-center justify-end gap-2'>
                            <BufferedTextInput
                                value={client.phone}
                                onDebouncedChange={value => updateField('phone', value)}
                                className='w-48'
                            />
                        </div>
                    </div>
                    <div className='flex items-center justify-between'>
                        <span className='flex items-center gap-2'>
                            <Mail className='h-4 w-4' />
                            {t('client.email', 'Email')}:
                        </span>
                        <div className='flex items-center justify-end gap-2'>
                            <BufferedTextInput
                                value={client.email}
                                onDebouncedChange={value => updateField('email', value)}
                                className='w-48'
                            />
                        </div>
                    </div>
                    <div className='flex items-center justify-between'>
                        <span className='flex items-center gap-2'>
                            <MapPin className='h-4 w-4' />
                            {t('client.address', 'Dirección')}:
                        </span>
                        <div className='flex items-center justify-end gap-2'>
                            <BufferedTextInput
                                value={client.address}
                                onDebouncedChange={value => updateField('address', value)}
                                className='w-48'
                            />
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}
